import { httpClient } from '@bs/core'
import type { CustomerEcomOrderIdType } from '../../../features/Customer/adapters/types'
import { mapInsightsExtras } from './insightsExtras'
import { spendBucketToClvRange } from './pantryCustomerApis'
import type {
  Customer,
  CustomerCategorySummary,
  CustomerExportParams,
  CustomerExportResponse,
  CustomerInsights,
  CustomerListParams,
  CustomerListResponse,
  CustomerNoteInput,
  CustomerOrder,
  CustomerOrderListParams,
  CustomerOrderListResponse,
  CustomerProductSummary,
  CustomerRewardListResponse,
  CustomerShippingMethod,
  CustomerTimelineItem,
  CustomerUpdatePayload,
} from '../../../types/customer'

// ── Greenline Customer gateway ────────────────────────────────────────────────
//
// Mirrors the legacy main-app hooks at:
//   src/pages/Customer/hooks/useGreenlineCustomerDetail.js
//   src/pages/Customer/hooks/useGreenlineCustomerNote.js
//   src/pages/Customer/hooks/useGreenlineCustomerList.js
//   src/pages/Customer/hooks/greenlineMapper.js
//
// Endpoint quirks specific to greenline (vs cova):
//   - Timeline path is `/timelines` (plural). Cova is `/timeline`.
//   - Delete note uses a path param: `…/timelines/{noteId}`. Cova uses
//     `?note_id=ID`.
//   - Insights call forces `time_zone=America/Vancouver` (the legacy hook
//     hard-codes it). Cova omits the param.
//   - POS orders hit `GET /greenline/stores/{storeId}/orders` with a query
//     string. Cova uses `POST /cova/orders/` with an action body.
//   - Sort key `fullname` maps to `name` (cova maps to `CustomerName`).
//
// Response envelope: `httpClient.json.*` already unwraps `res.data.data`.
//
// `// VERIFY:` comments flag response shapes inferred from legacy
// destructuring rather than a backend contract.

// ── URL builders ──────────────────────────────────────────────────────────────

const customerListUrl = (storeId: string) =>
  `/greenline/stores/${storeId}/customers`
const customerDetailUrl = (storeId: string, customerId: string) =>
  `/greenline/stores/${storeId}/customers/${customerId}`
const insightsUrl = (storeId: string, customerId: string) =>
  `/greenline/stores/${storeId}/customers/${customerId}/insights`
const timelinesUrl = (storeId: string, customerId: string) =>
  `/greenline/stores/${storeId}/customers/${customerId}/timelines`
const timelineDeleteUrl = (
  storeId: string,
  customerId: string,
  noteId: string,
) => `/greenline/stores/${storeId}/customers/${customerId}/timelines/${noteId}`
const exportUrl = (storeId: string) =>
  `/greenline/stores/${storeId}/customers/export`
const productSearchUrl = (storeId: string) =>
  `/greenline/stores/${storeId}/products/search`
const posOrdersUrl = (storeId: string) => `/greenline/stores/${storeId}/orders`

const ECOM_ORDERS_URL = '/stores/woocommerce/orders/'
const SHIPPING_METHODS_URL = '/stores/woocommerce/shipping/methods/'
const CATEGORIES_URL = '/stores/woocommerce/products/categories/'
const REWARDS_URL = '/stores/woocommerce/points_rewards'

// ── Wire shapes (confirmed against real API responses, 2026-05) ────────────────
//
// Real greenline list response uses snake_case with three address sources:
//   • `address` (flat, holds province/postalCode but `address`/`city` may be null)
//   • `shipping` / `billing` nested blocks (richer address_1/postcode/state)
// Display name lives on `name` (already concatenated); first/last names also
// appear at the top level. Loyalty info is just `loyaltyTierId` + `creditBalance`;
// there is no points field on the list response.

interface ApiGreenlineAddress {
  address?: string | null
  city?: string | null
  province?: string | null
  country?: string | null
  postalCode?: string | null
}

interface ApiGreenlineAddressBlock {
  first_name?: string
  last_name?: string
  company?: string
  address_1?: string | null
  address_2?: string | null
  city?: string | null
  state?: string | null
  postcode?: string | null
  country?: string | null
  phone?: string
  email?: string
}

interface ApiCustomerListItem {
  _id?: string
  id?: string | number
  company_id?: number
  companyId?: number
  woo_customer_id?: number | string
  woo_id?: string | number
  name?: string
  first_name?: string
  last_name?: string
  full_name?: string
  email?: string
  phone?: string
  username?: string | null
  birthday?: string | null
  _shipping_dob?: string | null
  _created?: string
  _updated?: string
  createDate?: string
  address?: ApiGreenlineAddress
  shipping?: ApiGreenlineAddressBlock
  billing?: ApiGreenlineAddressBlock
  total_orders?: number
  completed_orders?: number
  total_pos_orders?: number
  total_online_orders?: number
  total_order_items?: number
  clv?: number
  average_order_amount?: number | null
  average_order_items?: number | null
  last_order_date?: string
  // Most recent recorded storefront activity (any customer_event). ISO-8601
  // UTC with a trailing `Z`, or null/absent. Surfaced as the list-only
  // "Last Seen" column.
  last_activity_gmt?: string
  first_completed_order_date?: string
  loyaltyTierId?: number | string | null
  creditBalance?: number | null
  cardId?: string | null
  // Legacy / detail-endpoint passthrough fallbacks.
  date_of_birth?: string
  age_verified?: boolean
  address_line_1?: string
  address_line_2?: string
  city?: string
  state?: string
  postcode?: string
  country?: string
  aov?: number
  points?: number
  customer_since_date?: string
  loyalty_tier?: string
  [key: string]: unknown
}

interface ApiCustomerListResponse {
  items?: ApiCustomerListItem[]
  total_items?: number
  total_pages?: number
  page?: number
  limit?: number
  // Store-wide-but-filter-respecting aggregates computed by the backend over
  // the current search + filter set. `active_60d` = customers whose last order
  // was within 60 days; `new_30d` = customers who joined within 30 days.
  stats?: {
    total?: number
    active_60d?: number
    new_30d?: number
  }
}

interface ApiCustomerTimelineItem {
  // Greenline timeline rows use Mongo `_id` like cova; legacy code in
  // `useGreenlineCustomerDetailV2.ts` reads `record._id` too. Both keys are
  // declared so the mapper degrades gracefully if the shape ever shifts.
  _id?: string | number
  id?: string | number
  description?: string
  _created?: string
  _updated?: string
  operator_name?: string
  operator_photo?: string
  source?: string
  action?: string
}

interface ApiCustomerOrderShippingLine {
  method_id?: string
  method_title?: string
}

interface ApiCustomerOrderLineItem {
  parent_name?: string
  name?: string
  quantity?: number
}

interface ApiCustomerOrder {
  id?: string | number
  order_number?: string
  status?: string
  total?: number | string
  channel?: string
  // Legacy fields (kept for back-compat with older mocks/tests).
  fulfillment?: string
  date_created?: string
  items_preview?: string
  // Real woocommerce-shaped fields surfaced by `paginate_order`.
  item_count?: number
  line_items?: ApiCustomerOrderLineItem[]
  shipping_lines?: ApiCustomerOrderShippingLine[]
  fulfillment_status?: string
  fulfillment_type?: string
  fulfillment_list_type?: string
  location_name?: string
  date_created_gmt?: string
}

interface ApiCustomerOrderListResponse {
  items?: ApiCustomerOrder[]
  total_items?: number
  total_pages?: number
}

// Real greenline POS order shape (GET /greenline/stores/{storeId}/orders).
// This is NOT the woocommerce-shaped `ApiCustomerOrder` above — the POS
// endpoint returns its own fields (`customId`, `createDate`, `lines[]`,
// `total_items`, `orderType`), so it needs a dedicated mapper. Confirmed
// against a real greenline POS response, 2026-09-22.
interface ApiGreenlinePosOrderLine {
  productName?: string
  quantity?: number
}

interface ApiGreenlinePosOrder {
  id?: string
  customId?: string
  status?: string
  total?: number | string
  createDate?: string
  completeDate?: string
  orderType?: string
  location_name?: string
  total_items?: number
  lines?: ApiGreenlinePosOrderLine[]
}

interface ApiGreenlinePosOrderListResponse {
  items?: ApiGreenlinePosOrder[]
  total_items?: number
  total_pages?: number
}

interface ApiCustomerRewardLog {
  id?: string | number
  type?: 'earn' | 'redeem' | 'expire' | 'adjustment'
  points?: number
  balance_after?: number
  description?: string
  created_at?: string
}

interface ApiCustomerRewardLogResponse {
  items?: ApiCustomerRewardLog[]
  total_items?: number
  total_pages?: number
}

interface ApiCustomerFavoriteProduct {
  name?: string
  brand?: string | null
  category?: { id?: string | number; name?: string } | null
  category_product?: { id?: string | number; name?: string } | null
  catalog_id?: string
  variable_id?: number | string
  image?: string | null
  number_of_purchase?: number
  total_amount_spent?: number
  last_order_date?: string | null
}

interface ApiCustomerInsights {
  // VERIFY: cova's insights payload exposes `clv`, `average_order_value` and
  // `latest_order.CreatedTime`. The greenline shape isn't documented yet —
  // declared optionally here so the mapper degrades to `undefined` instead of
  // crashing when the fields are absent. The hero stats strip falls back to
  // the customer summary fields when these are missing.
  clv?: number
  average_order_value?: number
  latest_order?: {
    CreatedTime?: string
    [key: string]: unknown
  }

  // `top_5_preferences_product[]` — same shape as cova per the legacy
  // greenlineMapper at src/pages/Customer/hooks/greenlineMapper.js.
  top_5_preferences_product?: ApiCustomerFavoriteProduct[]

  retargeting_score?: number
  web_sessions_30d?: number
  top_searches?: Array<{ term: string; count: number }>
  abandoned_carts?: Array<{
    product_name: string
    price: number
    abandonedAt?: string
    abandoned_at?: string
  }>
  [key: string]: unknown
}

const mapFavoriteProduct = (
  p: ApiCustomerFavoriteProduct,
): import('../../../types/customer').CustomerFavoriteProduct => ({
  name: p.name ?? '—',
  brand: p.brand ?? null,
  category: p.category?.name ?? p.category_product?.name ?? null,
  catalogId: p.catalog_id,
  variableId: p.variable_id,
  imageUrl: p.image ?? null,
  numberOfPurchase: p.number_of_purchase ?? 0,
  totalAmountSpent: p.total_amount_spent ?? 0,
  lastOrderDate: p.last_order_date ?? null,
})

// ── Param mapping ─────────────────────────────────────────────────────────────

// Sort-key translation. Greenline accepts snake_case tokens that match the
// response fields (`name`, `clv`, `last_order_date`, `_created`, etc.). The
// legacy greenline mapper maps `fullname → name` (not `CustomerName` like
// cova). We extend the map so the new Customer-list UI keys
// (`lifetime_value`, `last_order_at`, `joined_at`) resolve against the real
// greenline fields.
const sortByMap: Record<string, string> = {
  // Legacy keys
  total_orders: 'total_orders',
  fullname: 'name',
  average_order_amount: 'average_order_amount',
  clv: 'clv',
  points: 'points',
  last_order_date: 'last_order_date',
  // New Customer-list UI keys (CUSTOMER_SORT_KEYS). No `loyalty_points` entry
  // — `points` was dropped from `CUSTOMER_SORT_KEYS` because the backend
  // never populates a sortable points field.
  lifetime_value: 'clv',
  last_order_at: 'last_order_date',
  joined_at: '_created',
}

const buildListQuery = (
  params: CustomerListParams,
): Record<string, unknown> => {
  const query: Record<string, unknown> = {
    page: params.page,
    limit: params.limit,
  }
  if (params.search) query.search = params.search
  if (params.sortBy) {
    query.sort_by = sortByMap[params.sortBy] ?? params.sortBy
    // Backend uses Mongo-style sort direction — '1' asc / '-1' desc — not
    // the 'asc'/'desc' tokens the UI works with. See the legacy
    // GreenlineCustomerListV2 `Sorting` wiring for the same convention.
    if (params.sort) query.sort = params.sort === 'asc' ? '1' : '-1'
  }
  // Greenline quirk: the list serializer's field is `location_ids` (a
  // ListField), not `location_id` like cova — confirmed against
  // `crm_be_v2_api/serializers/greenline/customers.py` and
  // `be_libs/utils/greenline/customers.py` (matched via `$in` against
  // `purchase_location_ids`). A single scalar value here still resolves to a
  // one-item list via Django's `QueryDict.getlist()`, so no array wrapping
  // is needed on the frontend.
  if (params.locationId) query.location_ids = params.locationId
  if (params.segment && params.segment !== 'all') query.segment = params.segment
  if (params.activity && params.activity !== 'all')
    query.activity = params.activity
  if (params.channel && params.channel !== 'all') query.channel = params.channel
  if (params.spendBucket && params.spendBucket !== 'all') {
    Object.assign(query, spendBucketToClvRange(params.spendBucket))
  }
  if (params.extra) Object.assign(query, params.extra)
  return query
}

// ── Mappers (API → domain) ────────────────────────────────────────────────────

// Coerce nullish/empty strings to `undefined` so the UI can render its
// `'—'` fallback instead of literal "null"/"" cells.
const nz = (v: string | null | undefined): string | undefined => {
  if (v == null) return undefined
  const trimmed = v.trim()
  return trimmed === '' ? undefined : trimmed
}

const mapCustomer = (raw: ApiCustomerListItem, storeId: string): Customer => {
  // Canonical id priority: greenline string `id` (e.g. "HyUImyc_6") → mongo
  // `_id` → legacy `woo_id` → `woo_customer_id`. Falls back to '' so the
  // row still renders rather than crashing the React key.
  const id = String(raw.id ?? raw._id ?? raw.woo_id ?? raw.woo_customer_id ?? '')

  const ship = raw.shipping ?? {}
  const bill = raw.billing ?? {}
  const addr = raw.address ?? {}

  const first =
    nz(raw.first_name) ?? nz(ship.first_name) ?? nz(bill.first_name) ?? ''
  const last =
    nz(raw.last_name) ?? nz(ship.last_name) ?? nz(bill.last_name) ?? ''
  const fullFromParts = [first, last].filter(Boolean).join(' ').trim()
  const full = nz(raw.name) ?? nz(raw.full_name) ?? fullFromParts ?? ''

  const address = {
    line1:
      nz(ship.address_1) ??
      nz(bill.address_1) ??
      nz(addr.address) ??
      nz(raw.address_line_1),
    line2:
      nz(ship.address_2) ?? nz(bill.address_2) ?? nz(raw.address_line_2),
    city: nz(ship.city) ?? nz(bill.city) ?? nz(addr.city) ?? nz(raw.city),
    province:
      nz(ship.state) ??
      nz(bill.state) ??
      nz(addr.province) ??
      nz(raw.state),
    postalCode:
      nz(ship.postcode) ??
      nz(bill.postcode) ??
      nz(addr.postalCode) ??
      nz(raw.postcode),
    country:
      nz(ship.country) ??
      nz(bill.country) ??
      nz(addr.country) ??
      nz(raw.country),
  }

  // Preserve woocommerce-style shipping/billing blocks for the edit dialog.
  const mapBlock = (
    block: ApiGreenlineAddressBlock | undefined,
  ): import('../../../types/customer').CustomerContactBlock | undefined =>
    block
      ? {
          firstName: nz(block.first_name),
          lastName: nz(block.last_name),
          company: nz(block.company),
          address1: nz(block.address_1),
          address2: nz(block.address_2),
          city: nz(block.city),
          state: nz(block.state),
          postcode: nz(block.postcode),
          country: nz(block.country),
          email: nz(block.email),
          phone: nz(block.phone),
        }
      : undefined

  return {
    id,
    storeId,
    storeType: 'greenline',
    firstName: first,
    lastName: last,
    fullName: full,
    email: nz(raw.email) ?? nz(bill.email) ?? '',
    phone: nz(raw.phone) ?? nz(ship.phone) ?? nz(bill.phone),
    dateOfBirth: raw.birthday ?? raw._shipping_dob ?? raw.date_of_birth,
    ageVerified: raw.age_verified,
    wooCustomerId: raw.woo_customer_id ?? raw.woo_id,
    address,
    shipping: mapBlock(raw.shipping),
    billing: mapBlock(raw.billing),
    totalOrders: raw.total_orders,
    lifetimeValue: raw.clv,
    avgOrderValue: raw.average_order_amount ?? raw.aov ?? undefined,
    // Greenline list response does NOT carry a loyalty points balance — only
    // `loyaltyTierId` and `creditBalance` (which is a dollar amount, not
    // points). Leave `loyaltyPoints` undefined so the table renders '0' rather
    // than misreporting credit as points.
    loyaltyPoints: raw.points ?? undefined,
    loyaltyTier:
      raw.loyalty_tier ??
      (raw.loyaltyTierId != null ? String(raw.loyaltyTierId) : undefined),
    lastOrderAt: raw.last_order_date,
    lastActivityGmt: raw.last_activity_gmt,
    joinedAt:
      raw._created ??
      raw.createDate ??
      raw.customer_since_date ??
      raw.first_completed_order_date,
    raw,
  }
}

// Greenline timeline mapper specifically omits avatar (legacy
// `greenlineCustomerTimelineMapper` drops `operator_photo`).
const mapTimelineItem = (
  raw: ApiCustomerTimelineItem,
): CustomerTimelineItem => ({
  id: String(raw._id ?? raw.id ?? ''),
  description: raw.description ?? '',
  createdAt: raw._created ?? raw._updated ?? '',
  operatorName: raw.operator_name,
  avatarUrl: undefined,
  source: raw.source,
  action: raw.action,
})

const titleFromId = (id: string): string =>
  id
    .split('_')
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ')

const deriveItemsPreview = (raw: ApiCustomerOrder): string | undefined => {
  if (raw.items_preview) return raw.items_preview
  const items = raw.line_items ?? []
  if (items.length === 0) return undefined
  const first = items[0]
  const firstName = first?.parent_name || first?.name || ''
  if (!firstName) return undefined
  const extra = items.length - 1
  return extra > 0 ? `${firstName} + ${extra} more` : firstName
}

const deriveFulfillmentLabel = (raw: ApiCustomerOrder): string | undefined => {
  const first = raw.shipping_lines?.[0]
  if (first?.method_title?.trim()) return first.method_title.trim()
  if (first?.method_id) return titleFromId(first.method_id)
  if (raw.fulfillment_type) return titleFromId(raw.fulfillment_type)
  return raw.fulfillment
}

const mapOrder = (raw: ApiCustomerOrder) => ({
  id: String(raw.id ?? ''),
  orderNumber: raw.order_number ?? String(raw.id ?? ''),
  channel: (raw.channel as 'online' | 'in_store' | 'both') ?? 'online',
  fulfillment:
    raw.fulfillment ?? raw.fulfillment_list_type ?? raw.fulfillment_status,
  fulfillmentLabel: deriveFulfillmentLabel(raw),
  locationName: raw.location_name,
  status: raw.status ?? '',
  total: typeof raw.total === 'string' ? Number(raw.total) : (raw.total ?? 0),
  createdAt: raw.date_created_gmt ?? raw.date_created ?? '',
  itemCount: raw.item_count ?? raw.line_items?.length,
  itemsPreview: deriveItemsPreview(raw),
  raw,
})

// Greenline POS orders (in-store) have their own wire shape, distinct from the
// woocommerce-shaped `mapOrder` above. The first line's `productName` drives the
// items preview; `lines.length - 1` is the "+ N more" tail.
const deriveGreenlinePosItemsPreview = (
  raw: ApiGreenlinePosOrder,
): string | undefined => {
  const items = raw.lines ?? []
  const firstName = items[0]?.productName ?? ''
  if (!firstName) return undefined
  const extra = items.length - 1
  return extra > 0 ? `${firstName} + ${extra} more` : firstName
}

// Dedicated greenline POS mapper (parallels cova's `mapPosOrder`).
//   • `orderNumber` = `customId` (e.g. "S0002225") — the human-facing receipt id
//     used for both the row label and the row-click redirect. `id` (a UUID) stays
//     the canonical record id.
//   • Fulfillment cell (product decision): the store location is the PRIMARY line
//     for every POS row and the order type — when present — is the SECONDARY line.
//     The shared OrdersTable renders `fulfillmentLabel` on top and `locationName`
//     underneath, so we map `location_name` into `fulfillmentLabel` (always shown)
//     and the title-cased `orderType` into `locationName` (shown only for delivery
//     etc.). Greenline POS orders have no woo/cova-style shipping-method
//     fulfillment, so this repurposes the two display slots rather than branching
//     inside the generic table.
const greenlineMapPosOrder = (raw: ApiGreenlinePosOrder): CustomerOrder => {
  const orderTypeLabel = raw.orderType ? titleFromId(raw.orderType) : undefined
  // Refund rows come back with a negative `total_items` (e.g. -2). The Items
  // column should always read as a positive count, so take the magnitude.
  const rawItemCount = raw.total_items ?? raw.lines?.length
  const itemCount = rawItemCount == null ? undefined : Math.abs(rawItemCount)
  return {
    id: String(raw.id ?? ''),
    orderNumber: raw.customId ?? String(raw.id ?? ''),
    channel: 'in_store' as const,
    fulfillment: raw.orderType,
    fulfillmentLabel: raw.location_name,
    locationName: orderTypeLabel,
    status: raw.status ?? '',
    total: typeof raw.total === 'string' ? Number(raw.total) : (raw.total ?? 0),
    createdAt: raw.createDate ?? raw.completeDate ?? '',
    itemCount,
    itemsPreview: deriveGreenlinePosItemsPreview(raw),
    raw,
  }
}

// ── Public gateway functions ──────────────────────────────────────────────────

export const greenlineListCustomers = async (
  storeId: string,
  params: CustomerListParams,
): Promise<CustomerListResponse> => {
  const raw = (await httpClient.json.get(customerListUrl(storeId), {
    params: buildListQuery(params),
  })) as ApiCustomerListResponse
  return {
    items: (raw?.items ?? []).map((item) => mapCustomer(item, storeId)),
    page: raw?.page ?? params.page,
    limit: raw?.limit ?? params.limit,
    totalItems: raw?.total_items ?? 0,
    totalPages: raw?.total_pages ?? 0,
    stats: {
      total: raw?.stats?.total ?? raw?.total_items ?? 0,
      active: raw?.stats?.active_60d ?? 0,
      new: raw?.stats?.new_30d ?? 0,
    },
  }
}

export const greenlineGetCustomer = async (
  storeId: string,
  customerId: string,
): Promise<Customer> => {
  const raw = (await httpClient.json.get(
    customerDetailUrl(storeId, customerId),
  )) as ApiCustomerListItem
  return mapCustomer(raw, storeId)
}

// Translate a domain CustomerContactBlock into the woocommerce-style block
// (snake_case) that the greenline PUT endpoint expects. Mirrors the legacy
// `revertDetailFormToApi` mapper in the main app.
//
// We coerce every field to a string (`?? ''`) so all 11 keys land on the
// wire even when the form value is empty. The backend treats the shipping
// block as a full replacement — sending only the populated keys would
// silently wipe the other fields. Matches legacy `revertDetailFormToApi`,
// which emitted all 11 keys verbatim from the form (and the form was
// pre-populated from the detail GET, never `undefined`).
const toApiBlock = (
  block?: import('../../../types/customer').CustomerContactBlock,
): Record<string, string> | undefined => {
  if (!block) return undefined
  return {
    first_name: block.firstName ?? '',
    last_name: block.lastName ?? '',
    company: block.company ?? '',
    address_1: block.address1 ?? '',
    address_2: block.address2 ?? '',
    city: block.city ?? '',
    postcode: block.postcode ?? '',
    country: block.country ?? '',
    state: block.state ?? '',
    email: block.email ?? '',
    phone: block.phone ?? '',
  }
}

export const greenlineUpdateCustomer = async (
  storeId: string,
  customerId: string,
  payload: CustomerUpdatePayload,
): Promise<Customer> => {
  // The PUT body mirrors the cova endpoint — woocommerce-style shipping +
  // billing blocks plus the legacy `_shipping_dob` field. Greenline expects
  // the same shape because it bridges into woo on the backend.
  const body: Record<string, unknown> = {}
  if (payload.firstName !== undefined) body.first_name = payload.firstName
  if (payload.lastName !== undefined) body.last_name = payload.lastName
  if (payload.email !== undefined) body.email = payload.email
  if (payload.phone !== undefined) body.phone = payload.phone
  if (payload.dateOfBirth !== undefined) body._shipping_dob = payload.dateOfBirth
  if (payload.gender !== undefined) body.gender = payload.gender

  const shippingBlock = toApiBlock(payload.shipping)
  if (shippingBlock) body.shipping = shippingBlock
  const billingBlock = toApiBlock(payload.billing)
  if (billingBlock) body.billing = billingBlock

  if (!shippingBlock && !billingBlock && payload.address) {
    body.shipping = {
      address_1: payload.address.line1,
      address_2: payload.address.line2,
      city: payload.address.city,
      state: payload.address.province,
      postcode: payload.address.postalCode,
      country: payload.address.country,
    }
  }

  if (payload.marketingEmail !== undefined)
    body.marketing_email = payload.marketingEmail
  if (payload.marketingSms !== undefined)
    body.marketing_sms = payload.marketingSms
  if (payload.extra) Object.assign(body, payload.extra)

  const raw = (await httpClient.json.put(
    customerDetailUrl(storeId, customerId),
    body,
  )) as ApiCustomerListItem
  return mapCustomer(raw, storeId)
}

// `resetPassword` moved to `pantryCustomerApis.ts::resetCustomerPassword` —
// the confirmed `/pantry/stores/:storeId/customers/:wooCustomerId/forgot-
// password` endpoint is identical across store types, so it no longer lives
// per-adapter here.

export const greenlineGetInsights = async (
  storeId: string,
  customerId: string,
  locationId?: string,
): Promise<CustomerInsights> => {
  const raw = (await httpClient.json.get(insightsUrl(storeId, customerId), {
    params: {
      location_id: locationId && locationId !== '-1' ? locationId : null,
      // Greenline quirk: legacy hook hard-codes this timezone.
      time_zone: 'America/Vancouver',
    },
  })) as ApiCustomerInsights
  return {
    clv: raw?.clv,
    averageOrderValue: raw?.average_order_value,
    latestOrderCreatedAt: raw?.latest_order?.CreatedTime,
    favoriteProducts: raw?.top_5_preferences_product?.map(mapFavoriteProduct),
    retargetingScore: raw?.retargeting_score,
    webSessions30d: raw?.web_sessions_30d,
    topSearches: raw?.top_searches,
    abandonedCarts: raw?.abandoned_carts?.map((c) => ({
      productName: c.product_name,
      price: c.price,
      abandonedAt: c.abandoned_at ?? c.abandonedAt ?? '',
    })),
    // Additive fields for the extended Behavior UI, parsed from the same
    // payload (channel split, brand/category affinity, order deltas). See
    // `insightsExtras.ts`.
    ...mapInsightsExtras(raw),
    raw,
  }
}

export const greenlineListTimeline = async (
  storeId: string,
  customerId: string,
  limit = 20,
): Promise<CustomerTimelineItem[]> => {
  const raw = (await httpClient.json.get(timelinesUrl(storeId, customerId), {
    params: { limit },
  })) as { items?: ApiCustomerTimelineItem[] } | ApiCustomerTimelineItem[]
  const items = Array.isArray(raw) ? raw : (raw?.items ?? [])
  return items.map(mapTimelineItem)
}

export const greenlineAddNote = async (
  storeId: string,
  customerId: string,
  note: CustomerNoteInput,
): Promise<CustomerTimelineItem> => {
  const raw = (await httpClient.json.post(timelinesUrl(storeId, customerId), {
    description: note.description,
  })) as ApiCustomerTimelineItem
  return mapTimelineItem(raw)
}

export const greenlineDeleteNote = async (
  storeId: string,
  customerId: string,
  noteId: string,
): Promise<void> => {
  // Greenline quirk: note id is a path segment, not a query param.
  await httpClient.json.delete(timelineDeleteUrl(storeId, customerId, noteId))
}

export const greenlineListEcomOrders = async (
  customerId: string,
  params: CustomerOrderListParams,
  idType: CustomerEcomOrderIdType = 'customer_id',
): Promise<CustomerOrderListResponse> => {
  // Greenline ecom orders still flow through the woocommerce orders action
  // endpoint (the legacy hook reuses the same path for all REST stores).
  const raw = (await httpClient.json.post(ECOM_ORDERS_URL, {
    action: 'paginate_order',
    parameters: {
      page: params.page,
      limit: params.limit,
      filters: { ...(params.filters ?? {}), [idType]: customerId },
      ...(params.sort ? { sort: params.sort } : {}),
    },
  })) as ApiCustomerOrderListResponse
  return {
    items: (raw?.items ?? []).map((item) => ({
      ...mapOrder(item),
      channel: 'online' as const,
    })),
    totalItems: raw?.total_items ?? 0,
    totalPages: raw?.total_pages ?? 0,
  }
}

export const greenlineListPosOrders = async (
  storeId: string,
  customerId: string,
  params: CustomerOrderListParams,
): Promise<CustomerOrderListResponse> => {
  const raw = (await httpClient.json.get(posOrdersUrl(storeId), {
    params: {
      page: params.page,
      limit: params.limit,
      customer_id: customerId,
      ...(params.sortBy ? { sort_by: params.sortBy } : {}),
      ...(params.sort ? { sort: params.sort } : {}),
      ...(params.filters ?? {}),
    },
  })) as ApiGreenlinePosOrderListResponse
  return {
    items: (raw?.items ?? []).map(greenlineMapPosOrder),
    totalItems: raw?.total_items ?? 0,
    totalPages: raw?.total_pages ?? 0,
  }
}

export const greenlineListRewards = async (
  customerId: string,
  params: { page: number; limit: number },
): Promise<CustomerRewardListResponse> => {
  const raw = (await httpClient.json.post(REWARDS_URL, {
    action: 'read_log',
    parameters: {
      page: params.page,
      limit: params.limit,
      filters: { customer_id: customerId },
    },
  })) as ApiCustomerRewardLogResponse
  return {
    items: (raw?.items ?? []).map((r) => ({
      id: String(r.id ?? ''),
      type: r.type ?? 'earn',
      points: r.points ?? 0,
      balanceAfter: r.balance_after,
      description: r.description,
      createdAt: r.created_at ?? '',
    })),
    totalItems: raw?.total_items ?? 0,
    totalPages: raw?.total_pages ?? 0,
  }
}

export const greenlineExportCsv = async (
  storeId: string,
  params: CustomerExportParams,
): Promise<CustomerExportResponse> => {
  // Legacy export is `GET /greenline/stores/{storeId}/customers/export`.
  // Backend (`crm_be_v2_api/views/greenline/customers.py:ExportCustomersView`)
  // returns either `{csv}` (inline) or `{export_id}` (queued async job) — same
  // contract as the cova counterpart. See covaExportCsv for the rationale on
  // pagination stripping and the `time_zone` snake-case parameter.
  const query = buildListQuery(params)
  delete query.page
  delete query.limit
  delete query.sort_by
  delete query.sort
  if (params.timeZone !== undefined) {
    query.time_zone = params.timeZone
  }
  return (await httpClient.json.get(exportUrl(storeId), {
    params: query,
  })) as unknown as CustomerExportResponse
}

export const greenlineListShippingMethods = async (
  storeId: string,
): Promise<CustomerShippingMethod[]> => {
  const raw = (await httpClient.json.post(SHIPPING_METHODS_URL, {
    store_id: storeId,
    action: 'read_direct_paginate',
    parameters: {},
  })) as
    | { items?: Array<{ id?: string | number; name?: string }> }
    | Array<{ id?: string | number; name?: string }>
  const list = Array.isArray(raw) ? raw : (raw?.items ?? [])
  return list.map((m) => ({ id: String(m.id ?? ''), name: m.name ?? '' }))
}

export const greenlineListCategories = async (): Promise<
  CustomerCategorySummary[]
> => {
  const raw = (await httpClient.json.post(CATEGORIES_URL, {
    action: 'list_all_only_name',
    parameters: {},
  })) as
    | Array<{ id?: string | number; name?: string }>
    | { items?: Array<{ id?: string | number; name?: string }> }
  const list = Array.isArray(raw) ? raw : (raw?.items ?? [])
  return list.map((c) => ({ id: String(c.id ?? ''), name: c.name ?? '' }))
}

export const greenlineSearchProducts = async (
  storeId: string,
  query: string,
): Promise<CustomerProductSummary[]> => {
  const raw = (await httpClient.json.get(productSearchUrl(storeId), {
    params: { search: query },
  })) as
    | Array<{ id?: string | number; name?: string }>
    | { items?: Array<{ id?: string | number; name?: string }> }
  const list = Array.isArray(raw) ? raw : (raw?.items ?? [])
  return list.map((p) => ({ id: String(p.id ?? ''), name: p.name ?? '' }))
}
